import { Attribute, Step, Group } from "@zakeke/zakeke-configurator-react";
import { ReactComponent as AngleLeftSolid } from "../../../assets/icons/angle-left-solid.svg";
import { ReactComponent as AngleRightSolid } from "../../../assets/icons/angle-right-solid.svg";
import textIcon from "../../../assets/icons/font-solid.svg";
import savedCompositionsIcon from "../../../assets/icons/saved_designs.svg";
import star from "../../../assets/icons/star.svg";

// import sizeImg from '../../assets/images/sizeCustom.png';

import OptionItem from "../../widgets/Option";
import Designer from "../Designer";

import { useZakeke } from "@zakeke/zakeke-configurator-react";
import { CarouselContainer, Icon } from "components/Atomic";
import {
  T,
  useActualGroups,
  useUndoRedoActions,
  useUndoRegister,
} from "Helpers";
import { Map } from "immutable";
import React, { useEffect, useState } from "react";
import useStore from "Store";
import styled from "styled-components";
import arrowDown from "../../../assets/icons/angle-down-solid.svg";
import arrowUp from "../../../assets/icons/angle-up-solid.svg";
import DesignsDraftList from "../DesignsDraftList";
import {
  ArrowIcon,
  AttributeAccordion,
  AttributeAccordionName,
  AttributeContainer,
  AttributeDescription,
  AttributeItem,
  AttributeName,
  AttributesContainer,
  GroupIcon,
  GroupItem,
  GroupsContainer,
  Options,
  OptionsContainer,
  OptionSelectedName,
  SelectorContainer,
} from "../LayoutStyled";
import Steps from "../Steps";
import CustomSizesComponent from "../CustomSizesComponent";

// const sizeGroup: Group = {
//     id: -4,
//     guid: "00000000-0000-0000-0000-000000000001",
//     name: "Size",
//     enabled: true,
//     imageUrl: "",
//     icon: null,
//     attributes: [],
//     steps: [],
//     cameraLocationId: "",
//     displayOrder: 4,
//     direction: 1,
//     attributesAlwaysOpened: true
// };

const SliderArrow = styled<
  React.FC<
    React.ComponentProps<typeof Icon> & { arrowDirection: "left" | "right" }
  >
>(Icon)`
  background: white;
  border: 1px #eee solid;
  border-radius: 3px;
  position: relative;

  ${(props) => props.arrowDirection === "left" && `left: -28px`}
  ${(props) => props.arrowDirection === "right" && `right: -28px`}
`;

const SelectorDesktop = () => {
  const {
    isSceneLoading,
    templates,
    currentTemplate,
    setCamera,
    setTemplate,
    draftCompositions,
  } = useZakeke();

  const {
    setSelectedGroupId,
    selectedGroupId,
    setSelectedStepId,
    selectedStepId,
    setSelectedAttributeId,
    selectedAttributeId,
    isUndo,
    isRedo,
  } = useStore();
  const [selectedCarouselSlide, setSelectedCarouselSlide] = useState<number>(0);
  const [attributesOpened, setAttributesOpened] = useState<
    Map<number, boolean>
  >(Map());
  const [isStartRegistering, setIsStartRegistering] = useState(false);

  const [lastSelectedSteps, setLastSelectedSteps] = useState(
    Map<number, number>()
  );
  const [
    lastSelectedAttributesFromGroups,
    setLastSelectedAttributesFromGroups,
  ] = useState(Map<number, number>());
  const [lastSelectedAttributesFromSteps, setLastSelectedAttributesFromSteps] =
    useState(Map<number, number>());
  const actualGroups = useActualGroups() ?? [];

  const selectedGroup = selectedGroupId
    ? actualGroups.find((group) => group.id === selectedGroupId)
    : null;
  const selectedStep = selectedGroupId
    ? actualGroups
        .find((group) => group.id === selectedGroupId)
        ?.steps.find((step) => step.id === selectedStepId)
    : null;
  const currentAttributes = selectedStep
    ? selectedStep.attributes
    : selectedGroup
    ? selectedGroup.attributes
    : [];
  const selectedAttribute = currentAttributes
    ? currentAttributes.find((attr) => attr.id === selectedAttributeId)
    : null;
  const undoRegistering = useUndoRegister();
  const undoRedoActions = useUndoRedoActions();
  const groupIndex =
    actualGroups && selectedGroup ? actualGroups.indexOf(selectedGroup) : 0;

  const swap = (group: Group[], i: number, j: number) => {
    let temp = group[i];
    group[i] = group[j];
    group[j] = temp;
    return group;
  };

  if (actualGroups.length > 1)
    swap(actualGroups, actualGroups.length - 2, actualGroups.length - 1);

  const handleNextStep = () => {
    if (selectedGroup) {
      if (groupIndex < actualGroups.length - 1) {
        const nextGroup = actualGroups[groupIndex + 1];
        handleGroupSelection(nextGroup.id);
      }
    }
  };

  const handlePreviousStep = () => {
    if (selectedGroup) {
      if (groupIndex > 0) {
        let previousGroup = actualGroups[groupIndex - 1];
        handleGroupSelection(previousGroup.id);

        // Select the last step
        if (previousGroup.steps.length > 0)
          handleStepSelection(
            previousGroup.steps[previousGroup.steps.length - 1].id
          );
      }
    }
  };

  const handleStepChange = (step: Step | null) => {
    if (step) handleStepSelection(step.id);
  };

  const handleGroupSelection = (groupId: number) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (groupId && selectedGroupId !== groupId && !isUndo && !isRedo) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "group",
        id: selectedGroupId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "group",
        id: groupId ?? null,
        direction: "redo",
      });
    }
    setSelectedGroupId(groupId);
  };

  const handleStepSelection = (stepId: number | null) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (selectedStepId !== stepId && !isUndo && !isRedo) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "step",
        id: selectedStepId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "step",
        id: stepId ?? null,
        direction: "redo",
      });
    }

    setSelectedStepId(stepId);

    const newStepSelected = lastSelectedSteps.set(selectedGroupId!, stepId!);
    setLastSelectedSteps(newStepSelected);
  };

  const handleAttributeSelection = (
    attributeId: number,
    isAttributesVertical?: boolean
  ) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (
      attributeId &&
      selectedAttributeId !== attributeId &&
      !isUndo &&
      !isRedo
    ) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "attribute",
        id: selectedAttributeId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "attribute",
        id: attributeId,
        direction: "redo",
      });
    }

    setSelectedAttributeId(attributeId);

    if (isAttributesVertical && !selectedGroup?.attributesAlwaysOpened) {
      setAttributesOpened(
        attributesOpened.set(attributeId, !attributesOpened.get(attributeId))
      );
    }

    if (
      selectedStep &&
      selectedStep.attributes.find((attr) => attr.id === attributeId)
    ) {
      const newLastAttributeSelected = lastSelectedAttributesFromSteps.set(
        selectedStepId!,
        attributeId
      );
      setLastSelectedAttributesFromSteps(newLastAttributeSelected);
    } else {
      const newLastAttributeSelected = lastSelectedAttributesFromGroups.set(
        selectedGroupId!,
        attributeId
      );
      setLastSelectedAttributesFromGroups(newLastAttributeSelected);
    }
  };

  // Initial template selection
  useEffect(() => {
    if (templates.length > 0 && !currentTemplate) setTemplate(templates[0].id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Initial group selection
  useEffect(() => {
    if (
      !isSceneLoading &&
      actualGroups &&
      actualGroups.length > 1 &&
      !selectedGroupId
    ) {
      handleGroupSelection(actualGroups[0].id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSceneLoading, actualGroups]);

  // Reset attribute selection when group selection changes
  useEffect(() => {
    if (selectedGroup && selectedGroup.id !== -2) {
      if (selectedGroup.steps.length > 0) {
        if (lastSelectedSteps.get(selectedGroupId!))
          handleStepSelection(lastSelectedSteps.get(selectedGroupId!)!);
        else handleStepSelection(selectedGroup.steps[0].id);
      } else {
        handleStepSelection(null);
      }

      setSelectedCarouselSlide(0);

      if (
        !actualGroups.find((group) => group.id === selectedGroupId)!
          .attributesAlwaysOpened
      ) {
        let attributes: Attribute[] = [];
        let group = actualGroups.find((group) => group.id === selectedGroupId)!;
        if (group.attributes.length > 0) {
          attributes.push(group.attributes[0]);
        }

        if (group.steps.length > 0) {
          let stepsFirstAttributes = group.steps.map((s) => {
            return s.attributes[0];
          });
          stepsFirstAttributes.forEach((attribute) => {
            attributes.push(attribute);
          });
        }

        if (attributes.length > 0) {
          // Add attributes in openedAttributes and set isOpened to true if already exists
          setAttributesOpened((prev) => {
            attributes.forEach((attribute) => {
              prev = prev.set(attribute.id, true);
            });

            return prev;
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId]);

  // check at any changes if there are attributes that needs to be opened
  useEffect(() => {
    if (actualGroups) {
      let attributes: Attribute[] = [];

      actualGroups.forEach((group) => {
        if (group && group.direction && group.direction === 1) {
          if (group.attributesAlwaysOpened)
            group.attributes
              .concat(group.steps.flatMap((s) => s.attributes))
              .map((attr) => attributes.push(attr));
        }
      });

      if (attributes.length > 0) {
        // Add attributes in openedAttributes and set isOpened to true if already exists
        setAttributesOpened((prev) => {
          attributes.forEach((attribute) => {
            prev = prev.set(attribute.id, true);
          });

          return prev;
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualGroups]);

  // Camera
  useEffect(() => {
    if (!isSceneLoading && selectedGroup && selectedGroup.cameraLocationId) {
      setCamera(selectedGroup.cameraLocationId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, isSceneLoading]);

  // select an attribute if selected step or group change
  useEffect(() => {
    if (selectedStep && selectedStep.attributes.length > 0) {
      if (lastSelectedAttributesFromSteps.get(selectedStepId!)) {
        const selectedAttributeId = lastSelectedAttributesFromSteps.get(
          selectedStepId!
        );
        if (
          selectedStep.attributes.some(
            (attr) => attr.id === selectedAttributeId
          )
        )
          handleAttributeSelection(
            lastSelectedAttributesFromSteps.get(selectedStepId!)!
          );
      } else handleAttributeSelection(selectedStep.attributes[0].id);
    } else if (selectedGroup && selectedGroup.attributes.length > 0) {
      if (
        selectedGroupId &&
        lastSelectedAttributesFromGroups.get(selectedGroupId) &&
        selectedGroup.attributes.some(
          (attr) =>
            attr.id === lastSelectedAttributesFromGroups.get(selectedGroupId)
        )
      )
        handleAttributeSelection(
          lastSelectedAttributesFromGroups.get(selectedGroupId!)!
        );
      else handleAttributeSelection(selectedGroup.attributes[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStepId, selectedGroupId]);

  useEffect(() => {
    if (isStartRegistering) {
      undoRegistering.endRegistering(false);
      setIsStartRegistering(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartRegistering]);

  return (
    <SelectorContainer>
      <GroupsContainer>
        {actualGroups.map((group) => {
          if (group)
            return (
              <GroupItem
                key={group.guid}
                className={
                  "group-item" +
                  (group.id === selectedGroupId ? " selected" : "")
                }
                onClick={() => handleGroupSelection(group.id)}
              >
                <GroupIcon
                  src={
                    group.imageUrl && group.imageUrl !== "" && group.id !== -2
                      ? group.id === -3
                        ? savedCompositionsIcon
                        : group.imageUrl
                      : group.id === -2
                      ? textIcon
                      : star
                  }
                />
                <span>
                  {group.name ? group.name : T._("Customize", "Composer")}
                </span>
              </GroupItem>
            );
          else return null;
        })}
      </GroupsContainer>
      <AttributesContainer>
        {/* Steps */}
        {selectedGroup &&
          selectedGroupId !== -2 &&
          selectedGroup.steps &&
          selectedGroup.steps.length > 0 && (
            <Steps
              key={"steps-" + selectedGroupId}
              hasNextGroup={groupIndex !== actualGroups.length - 1}
              hasPreviousGroup={groupIndex !== 0}
              onNextStep={handleNextStep}
              onPreviousStep={handlePreviousStep}
              currentStep={selectedStep}
              steps={selectedGroup.steps}
              onStepChange={handleStepChange}
            />
          )}

        {selectedGroupId &&
          selectedGroupId !== -2 &&
          selectedGroupId !== -3 && (
            <>
              {/* Attributes */}
              {selectedGroup?.direction === 0 && (
                <>
                  <CarouselContainer
                    key={selectedGroupId}
                    slidesToShow={window.innerWidth <= 1600 ? 3 : 4}
                    slideIndex={selectedCarouselSlide}
                    afterSlide={setSelectedCarouselSlide}
                    slidesToScroll={1}
                    speed={50}
                    renderBottomCenterControls={() => <span />}
                    renderCenterRightControls={({
                      nextSlide,
                      currentSlide,
                      slideCount,
                    }) =>
                      currentSlide + (window.innerWidth <= 1600 ? 3 : 4) >
                      slideCount - 1 ? null : (
                        <SliderArrow arrowDirection="right" onClick={nextSlide}>
                          <AngleRightSolid />
                        </SliderArrow>
                      )
                    }
                    renderCenterLeftControls={({
                      previousSlide,
                      currentSlide,
                      slideCount,
                    }) =>
                      currentSlide === 0 ? null : (
                        <SliderArrow
                          arrowDirection="left"
                          onClick={previousSlide}
                        >
                          <AngleLeftSolid />
                        </SliderArrow>
                      )
                    }
                  >
                    {currentAttributes &&
                      currentAttributes.map((attribute) => {
                        return (
                          <AttributeItem
                            selected={attribute.id === selectedAttributeId}
                            key={attribute.guid}
                            onClick={() =>
                              handleAttributeSelection(attribute.id)
                            }
                          >
                            <AttributeName>
                              {" "}
                              {attribute.name.toUpperCase()}{" "}
                            </AttributeName>
                            {attribute.groupId !== 8164 && (
                              <OptionSelectedName>
                                {attribute.options.find((opt) => opt.selected)
                                  ?.name ?? ""}
                              </OptionSelectedName>
                            )}
                          </AttributeItem>
                        );
                      })}
                  </CarouselContainer>

                  {(selectedAttribute &&
                    selectedAttribute.name.toLowerCase() === "size category") ||
                  selectedGroup.name.toLowerCase() !== "size" ? (
                    <>
                      <OptionsContainer key={"options-container"}>
                        <Options key={"option"}>
                          {selectedAttribute &&
                            selectedAttribute.options
                              .filter((x) => x.enabled)
                              .map((option) => (
                                <OptionItem
                                  selectAttribute={handleAttributeSelection}
                                  key={option.guid}
                                  selectedAttribute={selectedAttribute}
                                  option={option}
                                  hasDescriptionIcon={selectedAttribute.options.some(
                                    (x) => x.description
                                  )}
                                />
                              ))}
                        </Options>
                      </OptionsContainer>
                      <AttributeDescription>
                        {selectedAttribute?.description}
                      </AttributeDescription>
                    </>
                  ) : (
                    <CustomSizesComponent attribute={selectedAttribute!} />
                  )}
                </>
              )}
              {selectedGroup?.direction === 1 && (
                <>
                  {currentAttributes &&
                    currentAttributes.map((attribute) => {
                      return (
                        <AttributeContainer key={"container" + attribute.code}>
                          <AttributeAccordion
                            key={attribute.guid}
                            opened={attributesOpened.get(attribute.id)}
                            onClick={() =>
                              handleAttributeSelection(attribute.id, true)
                            }
                          >
                            <AttributeAccordionName>
                              {attribute.name}
                            </AttributeAccordionName>

                            {!selectedGroup.attributesAlwaysOpened && (
                              <ArrowIcon
                                key={"accordion-icon"}
                                src={
                                  attributesOpened.get(attribute.id)
                                    ? arrowUp
                                    : arrowDown
                                }
                              />
                            )}
                          </AttributeAccordion>

                          {attributesOpened.get(attribute.id) && (
                            <OptionsContainer>
                              <Options>
                                {attribute.options
                                  .filter((x) => x.enabled)
                                  .map((option) => (
                                    <OptionItem
                                      selectAttribute={handleAttributeSelection}
                                      key={option.guid}
                                      selectedAttribute={selectedAttribute}
                                      option={option}
                                      hasDescriptionIcon={attribute.options.some(
                                        (x) => x.description
                                      )}
                                    />
                                  ))}
                              </Options>
                            </OptionsContainer>
                          )}
                        </AttributeContainer>
                      );
                    })}
                </>
              )}
            </>
          )}

        {/* Designer / Customizer */}
        {selectedGroupId === -2 && <Designer />}

        {/* Saved Compositions */}
        {draftCompositions && selectedGroupId === -3 && <DesignsDraftList />}
        {/* 
        {selectedGroup?.name.toLowerCase() === "size" && (
          <CustomSizesComponent group={selectedGroup} />
        )} */}
      </AttributesContainer>
    </SelectorContainer>
  );
};

export default SelectorDesktop;
